import { useState } from 'react';
import { Table, Tag, Input, Select, Space, Badge } from 'antd';
import type { Batch, EntryDetail } from '@/types/nacha';
import { formatAmount, getTransactionTypeLabel } from '@/utils/export';

interface EntriesViewProps {
  batches: Batch[];
}

interface FlatEntry extends EntryDetail {
  batchNumber: string;
  companyName: string;
  secCode: string;
}

function EntriesView({ batches }: EntriesViewProps) {
  const [searchText, setSearchText] = useState('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<string>('all');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Flatten all entries
  const allEntries: FlatEntry[] = batches.flatMap((batch) =>
    batch.entries.map((entry) => ({
      ...entry,
      batchNumber: batch.header.batchNumber,
      companyName: batch.header.companyName,
      secCode: batch.header.standardEntryClassCode,
    }))
  );

  // Transaction code classification based on NACHA standard
  // Debits: 21, 26, 27, 28, 29, 36, 37, 38, 39
  // Credits: 22, 23, 24, 31, 32, 33, 34
  // 21/31 = Return/Dishonored Return, 26/36 = Return for debit
  const isDebitCode = (code: number): boolean => {
    return [21, 26, 27, 28, 29, 36, 37, 38, 39].includes(code);
  };
  
  const isCreditCode = (code: number): boolean => {
    return [22, 23, 24, 31, 32, 33, 34].includes(code);
  };

  // Filter data
  const filteredEntries = allEntries.filter((entry) => {
    if (!searchText) {
      let matchesType = true;
      if (transactionTypeFilter !== 'all') {
        const code = parseInt(entry.transactionCode);
        if (transactionTypeFilter === 'debit') {
          matchesType = isDebitCode(code);
        } else if (transactionTypeFilter === 'credit') {
          matchesType = isCreditCode(code);
        }
      }
      return matchesType;
    }

    const lowerSearchText = searchText.toLowerCase();
    
    // Search across all columns
    const matchesSearch =
      entry.batchNumber.includes(searchText) ||
      entry.companyName.toLowerCase().includes(lowerSearchText) ||
      getTransactionTypeLabel(entry.transactionCode).toLowerCase().includes(lowerSearchText) ||
      entry.receivingDFI.includes(searchText) ||
      entry.checkDigit.includes(searchText) ||
      entry.dfaAccountNumber.includes(searchText) ||
      formatAmount(entry.amount).includes(searchText) ||
      entry.individualName.toLowerCase().includes(lowerSearchText) ||
      entry.individualIdNumber.toLowerCase().includes(lowerSearchText) ||
      entry.traceNumber.includes(searchText) ||
      entry.secCode.toLowerCase().includes(lowerSearchText) ||
      (entry.addenda && entry.addenda.some(a => 
        a.paymentRelatedInformation.toLowerCase().includes(lowerSearchText)
      ));

    let matchesType = true;
    if (transactionTypeFilter !== 'all') {
      const code = parseInt(entry.transactionCode);
      if (transactionTypeFilter === 'debit') {
        matchesType = isDebitCode(code);
      } else if (transactionTypeFilter === 'credit') {
        matchesType = isCreditCode(code);
      }
    }

    return matchesSearch && matchesType;
  });

  const columns = [
    {
      title: 'Batch #',
      dataIndex: 'batchNumber',
      key: 'batchNumber',
      width: 90,
      fixed: 'left' as const,
    },
    {
      title: 'Company Name',
      dataIndex: 'companyName',
      key: 'companyName',
      width: 150,
      fixed: 'left' as const,
      ellipsis: {
        showTitle: true,
      },
    },
    {
      title: 'Transaction Type',
      dataIndex: 'transactionCode',
      key: 'transactionType',
      width: 200,
      render: (code: string) => {
        const codeNum = parseInt(code);
        const isDebit = isDebitCode(codeNum);
        const isCredit = isCreditCode(codeNum);
        return (
          <Tag color={isDebit ? 'red' : isCredit ? 'green' : 'orange'}>
            {getTransactionTypeLabel(code)}
          </Tag>
        );
      },
    },
    {
      title: 'Routing Number',
      dataIndex: 'receivingDFI',
      key: 'routingNumber',
      width: 140,
      render: (routing: string) => (
        <code style={{ fontSize: '12px' }}>{routing}</code>
      ),
    },
    {
      title: 'Account Number',
      dataIndex: 'dfaAccountNumber',
      key: 'accountNumber',
      width: 140,
      render: (account: string) => (
        <code style={{ fontSize: '12px' }}>{account}</code>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount: string, record: FlatEntry) => {
        const codeNum = parseInt(record.transactionCode);
        const isDebit = isDebitCode(codeNum);
        const isCredit = isCreditCode(codeNum);
        return (
          <span style={{ 
            color: isDebit ? '#cf1322' : isCredit ? '#3f8600' : '#fa8c16', 
            fontWeight: 'bold' 
          }}>
            ${formatAmount(amount)}
          </span>
        );
      },
      sorter: (a: FlatEntry, b: FlatEntry) => parseInt(a.amount) - parseInt(b.amount),
    },
    {
      title: 'Individual Name',
      dataIndex: 'individualName',
      key: 'individualName',
      width: 180,
      ellipsis: {
        showTitle: true,
      },
    },
    {
      title: 'Individual Id',
      dataIndex: 'individualIdNumber',
      key: 'individualId',
      width: 150,
      ellipsis: {
        showTitle: true,
      },
    },
    {
      title: 'Trace Number',
      dataIndex: 'traceNumber',
      key: 'traceNumber',
      width: 150,
      render: (trace: string) => (
        <code style={{ fontSize: '11px' }}>{trace}</code>
      ),
    },
    {
      title: 'Has Addenda',
      dataIndex: 'addenda',
      key: 'addenda',
      width: 120,
      align: 'center' as const,
      render: (addenda: unknown[]) => {
        if (addenda && addenda.length > 0) {
          return <Badge count={addenda.length} style={{ backgroundColor: '#52c41a' }} />;
        }
        return <span style={{ color: '#8c8c8c', fontSize: '12px' }}>-</span>;
      },
    },
  ];

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '16px' }}>
      <Space style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search all columns..."
          allowClear
          style={{ width: 300 }}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1); // Reset to first page when search changes
          }}
        />
        <Select
          value={transactionTypeFilter}
          onChange={(value) => {
            setTransactionTypeFilter(value);
            setCurrentPage(1); // Reset to first page when filter changes
          }}
          style={{ width: 150 }}
          virtual={false}
          dropdownStyle={{ minWidth: 150 }}
          transitionName=""
          dropdownMatchSelectWidth={false}
        >
          <Select.Option value="all">All</Select.Option>
          <Select.Option value="credit">Credit</Select.Option>
          <Select.Option value="debit">Debit</Select.Option>
        </Select>
      </Space>

      <Table
        key={`${transactionTypeFilter}-${searchText}-${currentPage}-${pageSize}`}
        columns={columns}
        dataSource={filteredEntries}
        rowKey={(record) => record.traceNumber}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '30', '40', '50'],
          showTotal: (total) => `Total ${total} entries`,
          onChange: (page) => setCurrentPage(page),
          onShowSizeChange: (_, size) => {
            setPageSize(size);
            setCurrentPage(1); // Reset to first page when page size changes
          },
        }}
        size="small"
        expandable={{
          expandedRowRender: (record) => {
            if (!record.addenda || record.addenda.length === 0) return null;
            return (
              <div className="addenda-container">
                <strong>Addenda Records:</strong>
                {record.addenda.map((addenda, index) => (
                  <div key={index} className="addenda-item">
                    {index + 1}. {addenda.paymentRelatedInformation}
                  </div>
                ))}
              </div>
            );
          },
          rowExpandable: (record) => !!record.addenda && record.addenda.length > 0,
        }}
      />

      <style>{`
        /* Fixed column background - Dark theme (opaque) */
        .ant-table-cell-fix-left {
          background-color: rgb(15, 23, 42) !important;
        }

        .ant-table-thead .ant-table-cell-fix-left {
          background-color: rgb(15, 23, 42) !important;
        }

        /* Fixed column background - Light theme (opaque) */
        [data-theme='light'] .ant-table-cell-fix-left {
          background-color: rgb(255, 255, 255) !important;
        }

        [data-theme='light'] .ant-table-thead .ant-table-cell-fix-left {
          background-color: rgb(250, 250, 250) !important;
        }

        /* Ensure fixed columns have proper z-index */
        .ant-table-cell-fix-left {
          z-index: 2;
        }

        .ant-table-thead .ant-table-cell-fix-left {
          z-index: 3;
        }

        /* Add shadow to fixed columns for better visual separation */
        .ant-table-cell-fix-left::after {
          position: absolute;
          top: 0;
          right: 0;
          bottom: -1px;
          width: 30px;
          transform: translateX(100%);
          transition: box-shadow 0.3s;
          content: '';
          pointer-events: none;
        }

        .ant-table-ping-right .ant-table-cell-fix-left::after {
          box-shadow: inset 10px 0 8px -8px rgba(0, 0, 0, 0.15);
        }

        [data-theme='light'] .ant-table-ping-right .ant-table-cell-fix-left::after {
          box-shadow: inset 10px 0 8px -8px rgba(0, 0, 0, 0.08);
        }

        /* Disable pagination select dropdown slide animation */
        .ant-select-dropdown-placement-bottomLeft,
        .ant-select-dropdown-placement-topLeft {
          animation-duration: 0s !important;
        }
        
        .ant-slide-up-enter,
        .ant-slide-up-appear,
        .ant-slide-up-leave {
          animation-duration: 0s !important;
        }

        /* Prevent text wrapping in table cells */
        .ant-table-cell {
          white-space: nowrap !important;
        }
      `}</style>
    </div>
  );
}

export default EntriesView;
